/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: ChangeHistoryGrid.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Data grid específico para Change History con filtros integrados
────────────────────────────────────────────────────────────────*/

import { History } from 'lucide-react';
import DataGridSimple from '../../../components/ui/DataGridSimple';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import DateRangePicker, { DateRange } from '../../../components/ui/DateRangePicker';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { ChangeHistoryFilterValues } from '../types/changeHistory.types';
import { ChangeActionMultiSelect } from './ChangeActionMultiSelect';
import { useUsersList } from '../hooks/useUsersList';

interface ChangeHistoryGridProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: any[];
  loading: boolean;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  searchQuery: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: ChangeHistoryFilterValues;
  onSearch: (query: string) => void;
  onSort: (column: string) => void;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (limit: number) => void;
  onColumnToggle: (key: string) => void;
  onFilterChange: (filters: ChangeHistoryFilterValues) => void;
}

export function ChangeHistoryGrid({
  data,
  columns,
  loading,
  pagination,
  searchQuery,
  sortBy,
  sortOrder,
  filters,
  onSearch,
  onSort,
  onPageChange,
  onItemsPerPageChange,
  onColumnToggle,
  onFilterChange,
}: ChangeHistoryGridProps) {
  const { t } = useI18n();
  const { users, loading: usersLoading } = useUsersList();

  // Build DateRange value from filters
  const dateRangeValue: DateRange = {
    startDate: filters.startDate ? filters.startDate.split('T')[0] : new Date().toISOString().split('T')[0],
    endDate:   filters.endDate   ? filters.endDate.split('T')[0]   : new Date().toISOString().split('T')[0],
    startTime: filters.startDate && filters.startDate.includes('T') ? filters.startDate.split('T')[1].slice(0, 5) : '00:00',
    endTime:   filters.endDate   && filters.endDate.includes('T')   ? filters.endDate.split('T')[1].slice(0, 5)   : '23:59',
  };

  return (
    <div className="change-history-wrapper">
      {/* Filters Card - PRIMERO */}
      <div className="pt-16">
        <div className="p-3 bg-card/75 backdrop-blur-md border border-border/50 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

            {/* Row 1: Date Range | Actions */}
            <Label className="text-left">
              {t('changeHistory.filters.dateRange') || 'Date Range'}
            </Label>
            <DateRangePicker
              value={dateRangeValue}
              onChange={(range: DateRange) => onFilterChange({
                ...filters,
                startDate: `${range.startDate}T${range.startTime || '00:00'}`,
                endDate:   `${range.endDate}T${range.endTime   || '23:59'}`,
              })}
            />
            <SimpleTooltip
              content={t('changeHistory.filters.action.tooltip')}
              title={t('changeHistory.filters.action.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('changeHistory.filters.action.label')}</Label>
            </SimpleTooltip>
            <ChangeActionMultiSelect
              selectedActions={filters.actions}
              onChange={(actions) => onFilterChange({ ...filters, actions })}
              placeholder={t('changeHistory.filters.action.all')}
            />

            {/* Row 2: User | Resource */}
            <SimpleTooltip
              content={t('changeHistory.filters.user.tooltip')}
              title={t('changeHistory.filters.user.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('changeHistory.filters.user.label')}</Label>
            </SimpleTooltip>
            <select
              value={filters.userId}
              onChange={(e) => onFilterChange({ ...filters, userId: e.target.value })}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm h-9"
              disabled={usersLoading}
            >
              <option value="">{usersLoading ? 'Loading...' : t('changeHistory.filters.user.all')}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id.toString()}>
                  {user.fullName ? `${user.fullName} (${user.username})` : user.username}
                </option>
              ))}
            </select>

            <SimpleTooltip
              content={t('changeHistory.filters.resource.tooltip')}
              title={t('changeHistory.filters.resource.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('changeHistory.filters.resource.label')}</Label>
            </SimpleTooltip>
            <Input
              type="text"
              value={filters.resource}
              onChange={(e) => onFilterChange({ ...filters, resource: e.target.value })}
              placeholder={t('changeHistory.filters.resource.placeholder')}
              className="h-9"
            />
          </div>
        </div>
      </div>

      {/* Data Grid - DESPUÉS */}
      <DataGridSimple
        data={data}
        columns={columns}
        loading={loading}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={onSearch}
        onSort={onSort}
        onPageChange={onPageChange}
        onItemsPerPageChange={onItemsPerPageChange}
        onColumnToggle={onColumnToggle}
        title={t('changeHistory.title')}
        titleIcon={<History className="h-5 w-5 text-primary" />}
        searchPlaceholder={t('changeHistory.searchPlaceholder')}
        emptyMessage={t('changeHistory.emptyMessage')}
      />

      {/* Global CSS to remove spacing between filter box and grid */}
      <style dangerouslySetInnerHTML={{__html: `
        .change-history-wrapper {
          display: flex;
          flex-direction: column;
          gap: 0;
        }
        .change-history-wrapper .space-y-6 > :not([hidden]) ~ :not([hidden]) {
          margin-top: 0 !important;
        }
      `}} />
    </div>
  );
}
